import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip, Button, CircularProgress, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';
import axios from 'axios';

const statusColors = { approved: 'success', pending: 'warning', rejected: 'error', invalid: 'default' };

const labelStyle = { fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.25 };
const valueStyle = { fontSize: '0.85rem', fontWeight: 500, fontFamily: 'Montserrat, sans-serif' };
const monoStyle = { fontSize: '0.8rem', fontWeight: 500, fontFamily: 'monospace' };

function ConversionDetails() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadConversionDetails();
  }, [leadId]);

  const loadConversionDetails = async () => {
    setLoading(true);
    try {
      const leadRes = await axios.get(`/admin/leads/${leadId}`);
      const leadData = leadRes.data?.data || leadRes.data;
      setLead(leadData);
    } catch (e) {
      console.error(e);
      setError('Failed to load conversion details');
    }
    setLoading(false);
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-GB', { 
    day: '2-digit', month: '2-digit', year: 'numeric', 
    hour: '2-digit', minute: '2-digit', second: '2-digit' 
  }) + ' CET' : '-';

  const getAttribution = () => {
    if (lead?.metadata?.manual_import) return 'Manual Import';
    if (lead?.tracking_type === 'pixel') return 'Pixel';
    if (lead?.tracking_type === 'postback') return 'Postback';
    return 'Postback';
  };

  const parseUserAgent = (ua) => {
    if (!ua) return { browser: '-', os: '-', device: '-', fullUA: '-' };
    const parser = new UAParser(ua);
    const result = parser.getResult();
    return {
      browser: result.browser.name ? `${result.browser.name} ${result.browser.version || ''}`.trim() : '-',
      os: result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : '-',
      device: result.device.type || 'Desktop',
      fullUA: ua
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lead) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back to Conversions
        </Button>
        <Typography color="error">{error || 'Conversion not found'}</Typography>
      </Box>
    );
  }

  const uaInfo = parseUserAgent(lead.conversion_ua || lead.user_agent);

  return (
    <Box sx={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Conversions /</Typography>
          <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'Montserrat, sans-serif' }}>
            Conversion Details: {leadId.substring(0, 8)}...{leadId.substring(leadId.length - 4)}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* General Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>General</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Status</Typography>
                  <Chip 
                    label={lead.status} 
                    color={statusColors[lead.status]} 
                    size="small"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      ...(lead.status === 'approved' && { bgcolor: 'success.main', color: 'white' })
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Event Name</Typography>
                  <Typography sx={valueStyle}>{lead.event_name || 'Base (CPL)'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={labelStyle}>Offer</Typography>
                  <Typography 
                    sx={{ ...valueStyle, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/campaigns/${lead.campaign_id}`)}
                  >
                    {lead.campaign_name || '-'} ({lead.campaign_id})
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Advertiser</Typography>
                  <Typography sx={valueStyle}>{lead.advertiser_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Partner</Typography>
                  <Typography 
                    sx={{ ...valueStyle, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => navigate(`/partners/${lead.partner_id}`)}
                  >
                    {lead.partner_name || '-'} ({lead.partner_id})
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography sx={labelStyle}>Conversion ID</Typography>
                  <Typography sx={monoStyle}>{lead.lead_id}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Source ID</Typography>
                  <Typography sx={valueStyle}>{lead.source_id || lead.click_source || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Creative ID</Typography>
                  <Typography sx={valueStyle}>{lead.creative_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Offer URL ID</Typography>
                  <Typography sx={valueStyle}>{lead.offer_url_id || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Attribution Method</Typography>
                  <Typography sx={valueStyle}>{getAttribution()}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Transaction Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>Transaction</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Conversion Timestamp</Typography>
                  <Typography sx={valueStyle}>{formatDate(lead.created_at)}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>IP Address (conversion)</Typography>
                  <Typography sx={monoStyle}>{lead.conversion_ip || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>Click Timestamp</Typography>
                  <Typography sx={valueStyle}>{formatDate(lead.click_date)}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography sx={labelStyle}>IP Address (session)</Typography>
                  <Typography sx={monoStyle}>{lead.session_ip || '-'}</Typography>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <Typography sx={labelStyle}>Revenue</Typography>
                  <Typography sx={{ ...valueStyle, color: 'success.main' }}>€{(parseFloat(lead.revenue) || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <Typography sx={labelStyle}>Payout</Typography>
                  <Typography sx={{ ...valueStyle, color: 'primary.main' }}>€{(parseFloat(lead.payout) || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={4} sm={1}>
                  <Typography sx={labelStyle}>Profit</Typography>
                  <Typography sx={{ ...valueStyle, color: (parseFloat(lead.revenue) - parseFloat(lead.payout)) >= 0 ? 'success.main' : 'error.main' }}>
                    €{((parseFloat(lead.revenue) || 0) - (parseFloat(lead.payout) || 0)).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={labelStyle}>Click Redirect URL</Typography>
                  <Typography sx={{ ...valueStyle, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {lead.offer_url || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Device & Browser Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>Device & Browser Information</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={labelStyle}>HTTP User Agent (Conversion)</Typography>
                  <Box sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {uaInfo.fullUA}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography sx={labelStyle}>Browser</Typography>
                  <Typography sx={valueStyle}>{uaInfo.browser}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography sx={labelStyle}>Device</Typography>
                  <Typography sx={valueStyle}>{uaInfo.device}</Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography sx={labelStyle}>OS</Typography>
                  <Typography sx={valueStyle}>{uaInfo.os}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sub IDs Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>Sub IDs</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>Sub1</Typography>
                  <Typography sx={valueStyle}>{lead.sub1 || lead.click_sub1 || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>Sub2</Typography>
                  <Typography sx={valueStyle}>{lead.sub2 || lead.click_sub2 || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>Sub3</Typography>
                  <Typography sx={valueStyle}>{lead.sub3 || lead.click_sub3 || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={labelStyle}>Sub4</Typography>
                  <Typography sx={valueStyle}>{lead.sub4 || lead.click_sub4 || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography sx={labelStyle}>Sub5</Typography>
                  <Typography sx={valueStyle}>{lead.sub5 || lead.click_sub5 || '-'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Click Details Section */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>Click Details</Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography sx={labelStyle}>Click ID</Typography>
                  <Typography sx={monoStyle}>{lead.click_id || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>Country</Typography>
                  <Typography sx={valueStyle}>{lead.country || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>City</Typography>
                  <Typography sx={valueStyle}>{lead.city || '-'}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={labelStyle}>Referrer</Typography>
                  <Typography sx={{ ...valueStyle, fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {lead.referrer || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Metadata Section */}
        {lead.metadata && Object.keys(lead.metadata).length > 0 && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: '0.95rem' }}>Additional Metadata</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem', fontFamily: 'monospace', overflow: 'auto' }}>
                    {JSON.stringify(lead.metadata, null, 2)}
                  </pre>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default ConversionDetails;
